import { usePageTitle } from "@/hooks/use-page-title";
import { useEffect, useState, useMemo, useCallback } from "react";
import { useDebounce } from "@/hooks/use-debounce";
import type { User as AppUser, ServiceType } from "@shared/schema";
import { useQuery, useMutation, useInfiniteQuery } from "@tanstack/react-query";
import { Link, useLocation, useSearch } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { isUnauthorizedError } from "@/lib/authUtils";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { parseDateOnly } from "@/lib/dateUtils";
import ResponsiveLayout from "@/components/layout/responsive-layout";
import CreateWorkOrderModal from "@/components/modals/create-work-order-modal";
import EditWorkOrderModal from "@/components/modals/edit-work-order-modal";
import NotesDialog from "@/components/notes/notes-dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, FileText, CheckCircle, Clock, AlertCircle, Search, Building2, Truck, Users, Filter, User, Calendar, ArrowUpDown, ChevronDown, ChevronUp, RotateCcw, Loader2 } from "lucide-react";
import { formatPhoneNumber } from "@/lib/utils";
import { track } from "@/lib/analytics";

interface WorkOrderListItem {
  id: number;
  workOrderNumber: string | null;
  contactId: number | null;
  companyId: number | null;
  carrierId: number | null;
  secondaryContactId: number | null;
  serviceType: number | string | null;
  status: string;
  description: string | null;
  assignedToUserId: string | null;
  estimatedCompletionDate: string | null;
  completedDate: Date | null;
  createdAt: Date | null;
  updatedAt: Date | null;
  serviceTypeName: string | null;
  carrierName: string | null;
  carrierDocketNumber: string | null;
  carrierDocketPrefix: string | null;
  carrierCompanyId: number | null;
  serviceBureauCompanyId: number | null;
  companyName: string | null;
  carrierDotNumber: string | null;
  assignedUserFirstName: string | null;
  assignedUserLastName: string | null;
  requestingContactFirstName: string | null;
  requestingContactLastName: string | null;
  requestingContactEmail: string | null;
  secondaryContactFirstName: string | null;
  secondaryContactLastName: string | null;
  latestStatusChangedAt: string | null;
}

// "all" is the sentinel value used by filter select inputs to mean "no filter applied".
// No real entity status / ID / UUID can equal this string, so no collision risk.
function filtersToQueryString(params: Record<string, string | string[] | number | boolean | null | undefined>): string {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "" || value === "all") continue;
    if (Array.isArray(value)) {
      for (const v of value) {
        if (v !== undefined && v !== null && v !== "") qs.append(key, String(v));
      }
    } else {
      qs.set(key, String(value));
    }
  }
  return qs.toString();
}

const WO_COL_STORAGE_KEY = 'dottie_wo_col_widths';
const WO_COL_DEFAULTS: Record<string, number> = {
  workOrder: 180, carrier: 200, service: 140,
  dot: 100, docket: 110, status: 170, age: 80,
  assigned: 160, updated: 110, notes: 80,
};

export default function WorkOrders() {
  usePageTitle("Work Orders");
  const { toast } = useToast();
  const { isAuthenticated, isLoading, user } = useAuth();
  const [location, setLocation] = useLocation();
  const searchString = useSearch();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState<any>(null);
  const [statusFilter, setStatusFilter] = useState<string[]>(["new", "collecting_inputs", "validating", "awaiting_payment", "preparing_documents", "awaiting_signatures", "submitted", "awaiting_client_filing", "work_complete"]);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 250);
  const [activeTab, setActiveTab] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    const t = p.get("tab");
    return (t === 'status' || t === 'carrier' || t === 'company' || t === 'contact' || t === 'date') ? t : 'mine';
  });
  const [viewMode, setViewMode] = useState<"grouped" | "list">("grouped");
  const [serviceTypeFilter, setServiceTypeFilter] = useState("all");
  const [assignedFilter, setAssignedFilter] = useState<string[]>([]);
  const [dateFromFilter, setDateFromFilter] = useState("");
  const [dateToFilter, setDateToFilter] = useState("");
  const [companyIdFilter, setCompanyIdFilter] = useState<number | null>(null);
  const [hasUrlFilters, setHasUrlFilters] = useState(false);
  const [dateSortOrder, setDateSortOrder] = useState<"newest" | "oldest">("newest");
  const [ageSortDir, setAgeSortDir] = useState<"asc" | "desc" | null>(null);

  const [colWidths, setColWidths] = useState<Record<string, number>>(() => {
    try {
      return { ...WO_COL_DEFAULTS, ...JSON.parse(localStorage.getItem(WO_COL_STORAGE_KEY) ?? '{}') };
    } catch { return { ...WO_COL_DEFAULTS }; }
  });

  const startResize = useCallback((col: string, e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = colWidths[col] ?? WO_COL_DEFAULTS[col] ?? 120;
    const onMove = (ev: MouseEvent) => {
      const next = Math.max(60, startW + ev.clientX - startX);
      setColWidths(prev => {
        const w = { ...prev, [col]: next };
        localStorage.setItem(WO_COL_STORAGE_KEY, JSON.stringify(w));
        return w;
      });
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [colWidths]);

  const resetColWidths = useCallback(() => {
    setColWidths({ ...WO_COL_DEFAULTS });
    localStorage.removeItem(WO_COL_STORAGE_KEY);
  }, []);

  useEffect(() => {
    if (showCreateModal) {
      track("work_order_create_opened", {
        source_page: location,
      });
    }
  }, [showCreateModal, location]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      toast({
        title: "Unauthorized",
        description: "You are logged out. Logging in again...",
        variant: "destructive",
      });
      setTimeout(() => {
        window.location.href = "/api/login";
      }, 500);
      return;
    }
  }, [isAuthenticated, isLoading, toast]);

  // Read URL parameters and apply filters (reactive: re-runs when search string changes)
  useEffect(() => {
    const params = new URLSearchParams(searchString);
    const serviceType = params.get("serviceType");
    const status = params.get("status");
    const dateFrom = params.get("dateFrom");
    const dateTo = params.get("dateTo");
    const companyId = params.get("companyId");

    let hasFilters = false;

    if (serviceType) {
      setServiceTypeFilter(serviceType);
      hasFilters = true;
    }

    if (status) {
      // Handle "open" as a special alias for open statuses
      if (status === "open") {
        setStatusFilter(["new", "collecting_inputs", "validating", "awaiting_payment", "preparing_documents", "awaiting_signatures", "submitted", "awaiting_client_filing", "work_complete"]);
      } else if (status.includes(",")) {
        // Handle comma-separated status values
        setStatusFilter(status.split(","));
      } else {
        setStatusFilter([status]);
      }
      hasFilters = true;
    }

    if (dateFrom) {
      setDateFromFilter(dateFrom);
      hasFilters = true;
    }

    if (dateTo) {
      setDateToFilter(dateTo);
      hasFilters = true;
    }

    if (companyId) {
      const parsedCompanyId = parseInt(companyId, 10);
      if (!isNaN(parsedCompanyId)) {
        setCompanyIdFilter(parsedCompanyId);
        hasFilters = true;
      }
    } else {
      // Clear company filter when companyId param is not present
      setCompanyIdFilter(null);
    }

    // Also apply tab param reactively
    const tabParam = params.get("tab");
    if (tabParam === 'status' || tabParam === 'carrier' || tabParam === 'company' || tabParam === 'contact' || tabParam === 'date') {
      setActiveTab(tabParam);
      setAssignedFilter([]);
    } else if (tabParam === 'mine' || !tabParam) {
      // Only reset to mine if user is known (let the mine-tab useEffect handle assignedFilter)
    }

    setHasUrlFilters(hasFilters);
  }, [searchString, location]);

  // Use lightweight list endpoint for fast page loads
  const {
    data: workOrdersInfinite,
    isLoading: workOrdersLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<{ data: WorkOrderListItem[]; nextCursor: string | null; totalCount?: number }>({
    queryKey: ["/api/work-orders/list", {
      search: debouncedSearch,
      status: statusFilter,
      serviceType: serviceTypeFilter,
      assignedTo: assignedFilter,
      dateFrom: dateFromFilter,
      dateTo: dateToFilter,
      companyId: companyIdFilter,
    }],
    queryFn: async ({ pageParam }) => {
      const resolvedAssigned = assignedFilter
        .map(f => f === "me" ? (user?.id ?? "") : f)
        .filter(Boolean);
      const qs = filtersToQueryString({
        search: debouncedSearch,
        status: statusFilter,
        serviceType: serviceTypeFilter === "all" ? undefined : serviceTypeFilter,
        assignedTo: resolvedAssigned,
        dateFrom: dateFromFilter || undefined,
        dateTo: dateToFilter || undefined,
        companyId: companyIdFilter ?? undefined,
        cursor: pageParam as string | undefined,
      });
      const res = await fetch(`/api/work-orders/list${qs ? `?${qs}` : ''}`, { credentials: 'include' });
      return res.json();
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    retry: false,
    staleTime: 30000,
  });

  const workOrders = useMemo(() =>
    workOrdersInfinite?.pages.flatMap(p => p.data) ?? [],
    [workOrdersInfinite]
  );
  const workOrdersTotalCount = workOrdersInfinite?.pages[0]?.totalCount ?? workOrders.length;

  // Initialise Mine-tab assigned filter once current user is known
  useEffect(() => {
    const tabParam = new URLSearchParams(searchString).get("tab");
    if (user?.id && activeTab === 'mine' && assignedFilter.length === 0 && tabParam !== 'status') {
      setAssignedFilter([user.id]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Keep users query for the assignment dropdown (uses assignable-users endpoint for staff access)
  const { data: users } = useQuery<AppUser[]>({
    queryKey: ["/api/assignable-users"],
    retry: false,
    staleTime: 60000, // Users don't change often
  });

  const { data: serviceTypes } = useQuery<ServiceType[]>({
    queryKey: ["/api/service-types"],
    retry: false,
    staleTime: 300000, // Service types rarely change
  });

  const createWorkOrderMutation = useMutation({
    mutationFn: async (workOrderData: any) => {
      const response = await apiRequest("POST", "/api/work-orders", workOrderData);
      return await response.json();
    },
    onSuccess: (createdWorkOrder) => {
      track("work_order_created", {
        source_page: location,
        work_order_id: createdWorkOrder?.id ?? null,
        company_id: createdWorkOrder?.companyId ?? null,
        carrier_id: createdWorkOrder?.carrierId ?? null,
      });
      if (Array.isArray(createdWorkOrder?.documentRequests) && createdWorkOrder.documentRequests.length > 0) {
        track("document_request_updated", {
          source_page: location,
          work_order_id: createdWorkOrder.id,
          company_id: createdWorkOrder.companyId ?? null,
          carrier_id: createdWorkOrder.carrierId ?? null,
          count: createdWorkOrder.documentRequests.length,
          action: "created_with_work_order",
        });
      }
      queryClient.invalidateQueries({ queryKey: ["/api/work-orders/list"] });
      queryClient.invalidateQueries({ queryKey: ["/api/work-orders"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stats"] });
      toast({
        title: "Success",
        description: "Work order created successfully",
      });
      setShowCreateModal(false);
      // Navigate to the newly created work order details page
      if (createdWorkOrder?.id) {
        setLocation(`/work-orders/${createdWorkOrder.id}`);
      }
    },
    onError: (error) => {
      if (isUnauthorizedError(error)) {
        toast({
          title: "Unauthorized",
          description: "You are logged out. Logging in again...",
          variant: "destructive",
        });
        setTimeout(() => {
          window.location.href = "/api/login";
        }, 500);
        return;
      }
      toast({
        title: "Error",
        description: "Failed to create work order",
        variant: "destructive",
      });
    },
  });

  const updateWorkOrderMutation = useMutation({
    mutationFn: async (workOrderData: any) => {
      await apiRequest("PUT", `/api/work-orders/${selectedWorkOrder.id}`, workOrderData);
    },
    onSuccess: (_data, variables) => {
      track("work_order_updated", {
        source_page: location,
        work_order_id: selectedWorkOrder?.id ?? null,
        company_id: variables?.companyId ?? selectedWorkOrder?.companyId ?? null,
        carrier_id: variables?.carrierId ?? selectedWorkOrder?.carrierId ?? null,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/work-orders/list"] });
      queryClient.invalidateQueries({ queryKey: ["/api/work-orders"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stats"] });
      toast({
        title: "Success",
        description: "Work order updated successfully",
      });
      setShowEditModal(false);
      setSelectedWorkOrder(null);
    },
    onError: (error) => {
      if (isUnauthorizedError(error)) {
        toast({
          title: "Unauthorized",
          description: "You are logged out. Logging in again...",
          variant: "destructive",
        });
        setTimeout(() => {
          window.location.href = "/api/login";
        }, 500);
        return;
      }
      toast({
        title: "Error",
        description: "Failed to update work order",
        variant: "destructive",
      });
    },
  });

  const updateAssignmentMutation = useMutation({
    mutationFn: async ({ workOrderId, assignedToUserId }: { workOrderId: number; assignedToUserId: string | null }) => {
      await apiRequest("PUT", `/api/work-orders/${workOrderId}`, { assignedToUserId });
    },
    onSuccess: (_data, variables) => {
      track("work_order_assignee_changed", {
        source_page: location,
        work_order_id: variables.workOrderId,
        company_id: null,
        carrier_id: null,
        assigned_to_user_id: variables.assignedToUserId,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/work-orders/list"] });
      queryClient.invalidateQueries({ queryKey: ["/api/work-orders"] });
      toast({
        title: "Success",
        description: "Assignment updated successfully",
      });
    },
    onError: (error) => {
      if (isUnauthorizedError(error)) {
        toast({
          title: "Unauthorized",
          description: "You are logged out. Logging in again...",
          variant: "destructive",
        });
        setTimeout(() => {
          window.location.href = "/api/login";
        }, 500);
        return;
      }
      toast({
        title: "Error",
        description: "Failed to update assignment",
        variant: "destructive",
      });
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ workOrderId, status }: { workOrderId: number; status: string }) => {
      await apiRequest("PUT", `/api/work-orders/${workOrderId}`, { status });
    },
    onSuccess: (_data, variables) => {
      track("work_order_status_changed", {
        source_page: location,
        work_order_id: variables.workOrderId,
        company_id: null,
        carrier_id: null,
        status: variables.status,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/work-orders/list"] });
      queryClient.invalidateQueries({ queryKey: ["/api/work-orders"] });
      toast({
        title: "Success",
        description: "Status updated successfully",
      });
    },
    onError: (error) => {
      if (isUnauthorizedError(error)) {
        toast({
          title: "Unauthorized",
          description: "You are logged out. Logging in again...",
          variant: "destructive",
        });
        setTimeout(() => {
          window.location.href = "/api/login";
        }, 500);
        return;
      }
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive",
      });
    },
  });

  if (isLoading || !isAuthenticated) {
    return <div className="min-h-screen bg-slate-50" />;
  }

  const handleCreateWorkOrder = (workOrderData: any) => {
    createWorkOrderMutation.mutate(workOrderData);
  };

  const handleUpdateWorkOrder = (workOrderData: any) => {
    updateWorkOrderMutation.mutate(workOrderData);
  };

  const handleEditWorkOrder = (workOrder: any) => {
    setSelectedWorkOrder(workOrder);
    setShowEditModal(true);
  };

  const handleAssignmentChange = (workOrderId: number, assignedToUserId: string | null) => {
    updateAssignmentMutation.mutate({ workOrderId, assignedToUserId });
  };

  const handleStatusChange = (workOrderId: number, status: string) => {
    updateStatusMutation.mutate({ workOrderId, status });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      // Client-action group (amber): client needs to act
      case "collecting_inputs":
      case "awaiting_payment":
      case "awaiting_signatures":
      case "awaiting_client_filing":
        return "bg-amber-100 text-amber-800";
      // Internal group (blue): DNA staff working
      case "new":
      case "validating":
      case "preparing_documents":
      case "work_complete":
        return "bg-blue-100 text-blue-800";
      // External group (purple): submitted to third parties
      case "submitted":
        return "bg-purple-100 text-purple-800";
      // Terminal group (gray)
      case "closed":
      case "cancelled":
        return "bg-gray-100 text-gray-500";
      // Legacy statuses
      case "completed":
        return "bg-blue-100 text-blue-800";
      case "in_progress":
        return "bg-amber-100 text-amber-800";
      case "awaiting_documents":
        return "bg-amber-100 text-amber-800";
      case "pending":
        return "bg-amber-100 text-amber-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "work_complete":
      case "closed":
      case "completed":
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case "collecting_inputs":
      case "in_progress":
        return <Clock className="w-5 h-5 text-yellow-600" />;
      case "awaiting_payment":
      case "awaiting_documents":
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      case "cancelled":
        return <FileText className="w-5 h-5 text-gray-400" />;
      default:
        return <FileText className="w-5 h-5 text-blue-600" />;
    }
  };

  const formatStatus = (status: string) => {
    return status.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatServiceType = (serviceType: string) => {
    const match = (serviceTypes as any[] | undefined)?.find((st: any) => st.id === serviceType || st.code === serviceType);
    if (match?.name) return match.name;
    return serviceType.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
  };

  // Server-side filtering handles all filter conditions
  const filteredWorkOrders = workOrders;

  // Memoized grouping by carrier - using inline field names
  const groupedByCarrier = useMemo(() => {
    return filteredWorkOrders.reduce((acc: any, order: any) => {
      if (!order.carrierId) return acc; // Skip orders without carrier
      const carrierId = order.carrierId;
      if (!acc[carrierId]) {
        acc[carrierId] = {
          carrier: {
            id: order.carrierId,
            name: order.carrierName,
            dotNumber: order.carrierDotNumber,
            docketNumber: order.carrierDocketNumber,
            docketPrefix: order.carrierDocketPrefix,
          },
          company: { id: order.serviceBureauCompanyId, name: order.companyName },
          workOrders: []
        };
      }
      acc[carrierId].workOrders.push(order);
      return acc;
    }, {});
  }, [filteredWorkOrders]);

  // Memoized grouping by company - using serviceBureauCompanyId as key
  const groupedByCompany = useMemo(() => {
    return filteredWorkOrders.reduce((acc: any, order: any) => {
      if (!order.serviceBureauCompanyId) return acc; // Skip orders without company
      const companyKey = order.serviceBureauCompanyId;
      if (!acc[companyKey]) {
        acc[companyKey] = {
          company: { id: order.serviceBureauCompanyId, name: order.companyName },
          carriers: new Map(),
          workOrders: []
        };
      }
      
      if (order.carrierId && !acc[companyKey].carriers.has(order.carrierId)) {
        acc[companyKey].carriers.set(order.carrierId, {
          id: order.carrierId,
          name: order.carrierName,
          docketNumber: order.carrierDocketNumber,
          docketPrefix: order.carrierDocketPrefix,
        });
      }
      
      acc[companyKey].workOrders.push(order);
      return acc;
    }, {});
  }, [filteredWorkOrders]);

  // Memoized grouping by requesting contacts - using inline field names
  const groupedByContact = useMemo(() => {
    return filteredWorkOrders.reduce((acc: any, order: any) => {
      const contactKey = order.requestingContactFirstName ? 
        `${order.requestingContactFirstName} ${order.requestingContactLastName}` : 
        "No Contact";
      
      if (!acc[contactKey]) {
        acc[contactKey] = {
          contact: order.requestingContactFirstName ? {
            firstName: order.requestingContactFirstName,
            lastName: order.requestingContactLastName,
            email: order.requestingContactEmail,
          } : null,
          workOrders: []
        };
      }
      
      acc[contactKey].workOrders.push(order);
      return acc;
    }, {});
  }, [filteredWorkOrders]);

  // Memoized filter for current user's work orders
  // Mine tab uses workOrders directly — server assignedTo filter is set when Mine tab is active
  const myWorkOrders = workOrders;

  const getEstimatedCompletion = (order: any) => {
    if (order.status === "completed") {
      return "Completed";
    }
    if (order.estimatedCompletionDate) {
      const date = parseDateOnly(order.estimatedCompletionDate);
      const now = new Date();
      const diffTime = date.getTime() - now.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays < 0) {
        return "Overdue";
      } else if (diffDays === 0) {
        return "Due today";
      } else {
        return `${diffDays} days left`;
      }
    }
    return "Not set";
  };

  // Compute age in days (0 = today) for a work order
  const getAgeDays = (order: any): number => {
    if (!order.latestStatusChangedAt) return 0;
    const raw = order.latestStatusChangedAt as string;
    const hasTimezone = /Z$|[+-]\d{2}:\d{2}$/.test(raw);
    const since = new Date(hasTimezone ? raw : raw + 'Z');
    if (isNaN(since.getTime())) return 0;
    return Math.max(0, Math.floor((Date.now() - since.getTime()) / 86_400_000));
  };

  // Resizable column header helper
  const renderHead = (label: string, col: string) => (
    <TableHead
      className="relative overflow-hidden select-none"
      style={{ width: colWidths[col] }}
    >
      <span className="block overflow-hidden text-ellipsis whitespace-nowrap pr-3">{label}</span>
      <div
        className="absolute right-0 top-0 h-full w-2 cursor-col-resize group/rh flex items-center justify-center"
        onMouseDown={(e) => startResize(col, e)}
      >
        <div className="w-px h-full bg-transparent group-hover/rh:bg-primary/40 transition-colors" />
      </div>
    </TableHead>
  );

  // Helper function to render work order table
  const renderWorkOrderTable = (orders: any[]) => {
    const sorted = ageSortDir
      ? [...orders].sort((a, b) => ageSortDir === 'asc' ? getAgeDays(a) - getAgeDays(b) : getAgeDays(b) - getAgeDays(a))
      : orders;
    const totalWidth = Object.values(colWidths).reduce((sum: number, w: number) => sum + w, 0);
    return (
      <div>
        <div className="flex justify-end mb-1 px-1">
          <button
            onClick={resetColWidths}
            title="Reset column widths to defaults"
            className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1 py-1 px-2 rounded hover:bg-gray-100 transition-colors"
          >
            <RotateCcw className="w-3 h-3" />
            Reset columns
          </button>
        </div>
        <div className="border rounded-lg overflow-hidden">
          <Table style={{ tableLayout: 'fixed', width: totalWidth, minWidth: '100%' }}>
            <TableHeader>
              <TableRow>
                {renderHead("Work Order", "workOrder")}
                {renderHead("Carrier", "carrier")}
                {renderHead("Service", "service")}
                {renderHead("DOT Number", "dot")}
                {renderHead("Docket Number", "docket")}
                {renderHead("Status", "status")}
                <TableHead
                  className="relative overflow-hidden select-none cursor-pointer hover:bg-gray-50"
                  style={{ width: colWidths["age"] }}
                  onClick={() => setAgeSortDir(d => d === 'asc' ? 'desc' : d === 'desc' ? null : 'asc')}
                >
                  <span className="flex items-center gap-1 pr-3 whitespace-nowrap">
                    Age
                    {ageSortDir === 'asc' ? <ChevronUp className="w-3 h-3 text-primary" /> :
                     ageSortDir === 'desc' ? <ChevronDown className="w-3 h-3 text-primary" /> :
                     <ArrowUpDown className="w-3 h-3 text-gray-400" />}
                  </span>
                  <div
                    className="absolute right-0 top-0 h-full w-2 cursor-col-resize group/rh flex items-center justify-center"
                    onMouseDown={(e) => { e.stopPropagation(); startResize("age", e); }}
                  >
                    <div className="w-px h-full bg-transparent group-hover/rh:bg-primary/40 transition-colors" />
                  </div>
                </TableHead>
                {renderHead("Assigned", "assigned")}
                {renderHead("Last Updated", "updated")}
                {renderHead("Notes", "notes")}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((order: any) => (
                <TableRow key={order.id} className="hover:bg-gray-50">
                  <TableCell className="font-medium align-top overflow-hidden">
                    <Link
                      href={`/work-orders/${order.id}`}
                      className="flex items-center gap-2 text-primary hover:underline max-w-full"
                      data-testid={`link-work-order-${order.id}`}
                    >
                      {getStatusIcon(order.status)}
                      <span className="whitespace-nowrap truncate">{order.workOrderNumber}</span>
                    </Link>
                  </TableCell>
                  <TableCell className="align-top overflow-hidden">
                    <div className="min-w-0">
                      {order.carrierId ? (
                        <Link
                          href={`/carriers/${order.carrierId}`}
                          className="font-medium block truncate max-w-full text-primary hover:underline"
                          data-testid={`link-carrier-${order.id}`}
                          title={order.carrierName || "No carrier"}
                        >
                          {order.carrierName || "No carrier"}
                        </Link>
                      ) : (
                        <span
                          className="font-medium block truncate max-w-full"
                          data-testid={`link-carrier-${order.id}`}
                          title="No carrier"
                        >
                          No carrier
                        </span>
                      )}
                      {order.serviceBureauCompanyId ? (
                        <Link
                          href={`/companies/${order.serviceBureauCompanyId}`}
                          className="text-sm block truncate max-w-full text-gray-600 hover:text-primary hover:underline"
                          data-testid={`link-company-${order.id}`}
                          title={order.companyName || "N/A"}
                        >
                          {order.companyName || "N/A"}
                        </Link>
                      ) : (
                        <span
                          className="text-sm block truncate max-w-full text-gray-500"
                          data-testid={`link-company-${order.id}`}
                          title={order.companyName || "N/A"}
                        >
                          {order.companyName || "N/A"}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="align-top overflow-hidden">
                    <span className="whitespace-nowrap">{order.serviceTypeName || formatServiceType(order.serviceType)}</span>
                  </TableCell>
                  <TableCell className="align-top overflow-hidden">
                    <span className="whitespace-nowrap">{order.carrierDotNumber || "Not set"}</span>
                  </TableCell>
                  <TableCell className="align-top overflow-hidden">
                    <span className="whitespace-nowrap">{order.carrierDocketNumber ? `${order.carrierDocketPrefix}-${order.carrierDocketNumber}` : "Not set"}</span>
                  </TableCell>
                  <TableCell className="align-top overflow-hidden">
                    {(user?.role === "admin" || user?.role === "staff") ? (
                      <Select
                        value={order.status}
                        onValueChange={(value) => handleStatusChange(order.id, value)}
                        data-testid={`select-status-${order.id}`}
                      >
                        <SelectTrigger className="w-full h-8 text-xs min-w-0">
                          <SelectValue>
                            <Badge className={getStatusColor(order.status)}>
                              {formatStatus(order.status)}
                            </Badge>
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="collecting_inputs">Collecting Inputs</SelectItem>
                          <SelectItem value="validating">Validating</SelectItem>
                          <SelectItem value="awaiting_payment">Awaiting Payment</SelectItem>
                          <SelectItem value="preparing_documents">Preparing Documents</SelectItem>
                          <SelectItem value="awaiting_signatures">Awaiting Signatures</SelectItem>
                          <SelectItem value="submitted">Submitted</SelectItem>
                          <SelectItem value="awaiting_client_filing">Awaiting Client Filing</SelectItem>
                          <SelectItem value="work_complete">Work Complete</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge className={getStatusColor(order.status)} data-testid={`badge-status-${order.id}`}>
                        {formatStatus(order.status)}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="align-top overflow-hidden text-sm text-gray-600 tabular-nums">
                    {getAgeDays(order)}d
                  </TableCell>
                  <TableCell className="align-top overflow-hidden">
                    <div className="flex items-center gap-1 min-w-0">
                      <User className="w-4 h-4 text-gray-400 shrink-0" />
                      <Select
                        value={order.assignedToUserId || "unassigned"}
                        onValueChange={(value) => {
                          const userId = value === "unassigned" ? null : value;
                          handleAssignmentChange(order.id, userId);
                        }}
                      >
                        <SelectTrigger className="w-full h-8 text-xs min-w-0">
                          <SelectValue>
                            {order.assignedUserFirstName ? 
                              `${order.assignedUserFirstName} ${order.assignedUserLastName}` : 
                              "Not assigned"
                            }
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unassigned">Not assigned</SelectItem>
                          {users?.map((u: any) => (
                            <SelectItem key={u.id} value={u.id}>
                              {u.firstName} {u.lastName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-gray-500 align-top overflow-hidden">
                    <span className="whitespace-nowrap">
                      {order.updatedAt ? new Date(order.updatedAt).toLocaleDateString() : new Date(order.createdAt).toLocaleDateString()}
                    </span>
                  </TableCell>
                  <TableCell className="align-top overflow-hidden">
                    <NotesDialog
                      entityType="work_order"
                      entityId={order.id.toString()}
                      entityName={`Work Order ${order.workOrderNumber}`}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  };

  return (
    <ResponsiveLayout title="Work Orders" subtitle="Manage and track all work orders" currentPage="work-orders">
      <div className="space-y-6">
        {/* Enhanced filters and search */}
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Work Orders</h2>
              <p className="text-sm text-gray-600">Manage and track all work orders</p>
            </div>
            <Button onClick={() => setShowCreateModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Work Order
            </Button>
          </div>
          
          <div className="flex flex-wrap gap-4 items-end">
              <div className="relative flex-1 min-w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search work orders, carriers, companies..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="input-search-work-orders"
                />
              </div>
              
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-gray-500">Status</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-48 justify-between" data-testid="select-status-filter">
                      <span className="truncate">
                        {statusFilter.length === 0 ? "All" : 
                         statusFilter.length === 1 ? formatStatus(statusFilter[0]) :
                         `${statusFilter.length} selected`}
                      </span>
                      <ChevronDown className="w-4 h-4 ml-2 shrink-0" />
                    </Button>
                  </PopoverTrigger>
                <PopoverContent className="w-56 p-2" align="start">
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2 pb-2 border-b">
                      <Checkbox 
                        id="status-all"
                        checked={statusFilter.length === 0}
                        onCheckedChange={(checked) => {
                          if (checked) setStatusFilter([]);
                        }}
                      />
                      <label htmlFor="status-all" className="text-sm cursor-pointer">All Status</label>
                    </div>
                    {["new", "collecting_inputs", "validating", "awaiting_payment", "preparing_documents", "awaiting_signatures", "submitted", "awaiting_client_filing", "work_complete", "closed", "cancelled"].map((status) => (
                      <div key={status} className="flex items-center space-x-2">
                        <Checkbox 
                          id={`status-${status}`}
                          checked={statusFilter.includes(status)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setStatusFilter([...statusFilter, status]);
                            } else {
                              setStatusFilter(statusFilter.filter(s => s !== status));
                            }
                          }}
                        />
                        <label htmlFor={`status-${status}`} className="text-sm cursor-pointer">{formatStatus(status)}</label>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
                </Popover>
              </div>
              
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-gray-500">Service</label>
                <Select value={serviceTypeFilter} onValueChange={setServiceTypeFilter}>
                  <SelectTrigger className="w-48" data-testid="select-service-type-filter">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {serviceTypes?.map((st: any) => (
                      <SelectItem key={st.id} value={st.id}>
                        {st.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-gray-500">Assigned</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-48 justify-between" data-testid="select-assigned-filter">
                      <span className="truncate">
                        {assignedFilter.length === 0 ? "All" : 
                         assignedFilter.length === 1 ? 
                           (assignedFilter[0] === "me" ? "Me" :
                            assignedFilter[0] === "unassigned" ? "Unassigned" :
                            users?.find((u: any) => u.id === assignedFilter[0])?.firstName || "1 selected") :
                         `${assignedFilter.length} selected`}
                      </span>
                      <ChevronDown className="w-4 h-4 ml-2 shrink-0" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-56 p-2" align="start">
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2 pb-2 border-b">
                        <Checkbox 
                          id="assigned-all"
                          checked={assignedFilter.length === 0}
                          onCheckedChange={(checked) => {
                            if (checked) setAssignedFilter([]);
                          }}
                        />
                        <label htmlFor="assigned-all" className="text-sm cursor-pointer">All</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="assigned-me"
                          checked={assignedFilter.includes("me")}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setAssignedFilter([...assignedFilter, "me"]);
                            } else {
                              setAssignedFilter(assignedFilter.filter(a => a !== "me"));
                            }
                          }}
                        />
                        <label htmlFor="assigned-me" className="text-sm cursor-pointer">Assigned to Me</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="assigned-unassigned"
                          checked={assignedFilter.includes("unassigned")}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setAssignedFilter([...assignedFilter, "unassigned"]);
                            } else {
                              setAssignedFilter(assignedFilter.filter(a => a !== "unassigned"));
                            }
                          }}
                        />
                        <label htmlFor="assigned-unassigned" className="text-sm cursor-pointer">Unassigned</label>
                      </div>
                      {(users?.filter((u: any) => u && u.id) ?? []).length > 0 && (
                        <>
                          <div className="border-t pt-2 mt-2">
                            <p className="text-xs text-muted-foreground mb-2">Team Members</p>
                          </div>
                          {users?.filter((u: any) => u && u.id).map((u: any) => (
                            <div key={u.id} className="flex items-center space-x-2">
                              <Checkbox 
                                id={`assigned-${u.id}`}
                                checked={assignedFilter.includes(u.id)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setAssignedFilter([...assignedFilter, u.id]);
                                  } else {
                                    setAssignedFilter(assignedFilter.filter(a => a !== u.id));
                                  }
                                }}
                              />
                              <label htmlFor={`assigned-${u.id}`} className="text-sm cursor-pointer">{u.firstName} {u.lastName}</label>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            
            {/* Active Filters Display */}
            {hasUrlFilters && (
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-sm font-medium text-gray-700">Active Filters:</span>
                {companyIdFilter !== null && (
                  <Badge variant="secondary" className="gap-1" data-testid="badge-filter-company">
                    Company: {workOrders?.find((wo: any) => 
                      wo.companyId === companyIdFilter || 
                      wo.serviceBureauCompanyId === companyIdFilter ||
                      wo.carrierCompanyId === companyIdFilter
                    )?.companyName || `ID ${companyIdFilter}`}
                    <button
                      onClick={() => {
                        setCompanyIdFilter(null);
                        // Update URL to remove companyId param
                        const params = new URLSearchParams(window.location.search);
                        params.delete("companyId");
                        const newUrl = params.toString() ? `/work-orders?${params.toString()}` : "/work-orders";
                        setLocation(newUrl);
                      }}
                      className="ml-1 hover:text-red-600"
                      data-testid="button-clear-company-filter"
                    >
                      ×
                    </button>
                  </Badge>
                )}
                {serviceTypeFilter !== "all" && (
                  <Badge variant="secondary" className="gap-1" data-testid="badge-filter-service-type">
                    Service: {formatServiceType(serviceTypeFilter)}
                    <button
                      onClick={() => setServiceTypeFilter("all")}
                      className="ml-1 hover:text-red-600"
                      data-testid="button-clear-service-type-filter"
                    >
                      ×
                    </button>
                  </Badge>
                )}
                {dateFromFilter && (
                  <Badge variant="secondary" className="gap-1" data-testid="badge-filter-date-from">
                    From: {new Date(dateFromFilter).toLocaleDateString()}
                    <button
                      onClick={() => setDateFromFilter("")}
                      className="ml-1 hover:text-red-600"
                      data-testid="button-clear-date-from-filter"
                    >
                      ×
                    </button>
                  </Badge>
                )}
                {dateToFilter && (
                  <Badge variant="secondary" className="gap-1" data-testid="badge-filter-date-to">
                    To: {new Date(dateToFilter).toLocaleDateString()}
                    <button
                      onClick={() => setDateToFilter("")}
                      className="ml-1 hover:text-red-600"
                      data-testid="button-clear-date-to-filter"
                    >
                      ×
                    </button>
                  </Badge>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setServiceTypeFilter("all");
                    setStatusFilter(["new", "collecting_inputs", "validating", "awaiting_payment", "preparing_documents", "awaiting_signatures", "submitted", "awaiting_client_filing", "work_complete"]);
                    setDateFromFilter("");
                    setDateToFilter("");
                    setCompanyIdFilter(null);
                    setHasUrlFilters(false);
                    setLocation("/work-orders");
                  }}
                  className="text-sm"
                  data-testid="button-clear-all-filters"
                >
                  Clear All
                </Button>
              </div>
            )}
          </div>
          
          <Tabs value={activeTab} onValueChange={(tab) => {
              setActiveTab(tab);
              if (tab === 'mine' && user?.id) setAssignedFilter([user.id]);
              else if (tab !== 'mine') setAssignedFilter([]);
            }}>
              <TabsList className="grid w-full grid-cols-6">
                <TabsTrigger value="mine" className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  My Work Orders
                </TabsTrigger>
                <TabsTrigger value="carrier" className="flex items-center gap-2">
                  <Truck className="w-4 h-4" />
                  By Carrier
                </TabsTrigger>
                <TabsTrigger value="company" className="flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  By Company
                </TabsTrigger>
                <TabsTrigger value="contact" className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  By Contact
                </TabsTrigger>
                <TabsTrigger value="status" className="flex items-center gap-2">
                  <Filter className="w-4 h-4" />
                  By Status
                </TabsTrigger>
                <TabsTrigger value="date" className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  By Date
                </TabsTrigger>
              </TabsList>

          {workOrdersLoading ? (
            <div className="text-center py-8">
              <p className="text-gray-500">Loading work orders...</p>
            </div>
          ) : filteredWorkOrders.length === 0 ? (
            <Card className="mt-6">
              <CardContent className="pt-6">
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 mb-4">
                    {workOrders?.length === 0 ? "No work orders found" : "No work orders match your filters"}
                  </p>
                  {workOrders?.length === 0 && (
                    <Button onClick={() => setShowCreateModal(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Create First Work Order
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (<>
              
              {/* My Work Orders */}
              <TabsContent value="mine" className="mt-6">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-3">
                      <User className="w-5 h-5 text-primary" />
                      <div>
                        <h3 className="text-lg font-semibold">My Work Orders</h3>
                        <p className="text-sm text-gray-500 font-normal">
                          {myWorkOrders.length} work orders assigned to you
                        </p>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {myWorkOrders.length === 0 ? (
                      <div className="text-center py-8">
                        <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500">No work orders assigned to you</p>
                      </div>
                    ) : (
                      renderWorkOrderTable(myWorkOrders)
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              
              {/* Group by Carrier */}
              <TabsContent value="carrier" className="mt-6">
                <div className="space-y-6">
                  {Object.values(groupedByCarrier).map((group: any) => (
                    <Card key={group.carrier.id}>
                      <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-3">
                          <Truck className="w-5 h-5 text-blue-600" />
                          <div>
                            <h3 className="text-lg font-semibold">{group.carrier.name}</h3>
                            <p className="text-sm text-gray-500 font-normal">
                              {group.company?.name || "No company"} • DOT: {group.carrier.dotNumber || "Not set"}
                            </p>
                          </div>
                          <Badge variant="outline" className="ml-auto">
                            {group.workOrders.length} orders
                          </Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {renderWorkOrderTable(group.workOrders)}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>
              
              {/* Group by Company */}
              <TabsContent value="company" className="mt-6">
                <div className="space-y-6">
                  {Object.values(groupedByCompany).map((group: any) => (
                    <Card key={group.company.id}>
                      <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-3">
                          <Building2 className="w-5 h-5 text-green-600" />
                          <div>
                            <h3 className="text-lg font-semibold">{group.company.name}</h3>
                            <p className="text-sm text-gray-500 font-normal">
                              {group.carriers.size} carriers • {group.workOrders.length} orders
                            </p>
                          </div>
                          <Badge variant="outline" className="ml-auto">
                            {group.workOrders.length} orders
                          </Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {renderWorkOrderTable(group.workOrders)}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>
              
              {/* Group by Contact */}
              <TabsContent value="contact" className="mt-6">
                <div className="space-y-6">
                  {Object.entries(groupedByContact).map(([contactName, group]: [string, any]) => (
                    <Card key={contactName}>
                      <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-3">
                          <Users className="w-5 h-5 text-purple-600" />
                          <div>
                            <h3 className="text-lg font-semibold">{contactName}</h3>
                            <p className="text-sm text-gray-500 font-normal">
                              {group.contact?.email || "No email"} • {formatPhoneNumber(group.contact?.phone) || "No phone"}
                            </p>
                          </div>
                          <Badge variant="outline" className="ml-auto">
                            {group.workOrders.length} orders
                          </Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {renderWorkOrderTable(group.workOrders)}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>
              
              {/* Group by Status */}
              <TabsContent value="status" className="mt-6">
                <div className="space-y-6">
                  {["new", "collecting_inputs", "validating", "awaiting_payment", "preparing_documents", "awaiting_signatures", "submitted", "awaiting_client_filing", "work_complete", "closed", "cancelled"].map((status) => {
                    const statusOrders = filteredWorkOrders.filter((order: any) => order.status === status);
                    if (statusOrders.length === 0) return null;
                    
                    return (
                      <Card key={status}>
                        <CardHeader className="pb-3">
                          <CardTitle className="flex items-center gap-3">
                            {getStatusIcon(status)}
                            <div>
                              <h3 className="text-lg font-semibold">{formatStatus(status)}</h3>
                              <p className="text-sm text-gray-500 font-normal">
                                {statusOrders.length} work orders
                              </p>
                            </div>
                            <Badge variant="outline" className="ml-auto">
                              {statusOrders.length} orders
                            </Badge>
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          {renderWorkOrderTable(statusOrders)}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </TabsContent>
              
              {/* By Date */}
              <TabsContent value="date" className="mt-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-600">
                      {filteredWorkOrders.length} work orders sorted by {dateSortOrder === "newest" ? "newest first" : "oldest first"}
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDateSortOrder(dateSortOrder === "newest" ? "oldest" : "newest")}
                      className="flex items-center gap-2"
                      data-testid="button-toggle-date-sort"
                    >
                      <ArrowUpDown className="w-4 h-4" />
                      {dateSortOrder === "newest" ? "Newest First" : "Oldest First"}
                    </Button>
                  </div>
                  <Card>
                    <CardContent className="pt-4">
                      {renderWorkOrderTable(
                        [...filteredWorkOrders].sort((a: any, b: any) => {
                          const dateA = new Date(a.createdAt).getTime();
                          const dateB = new Date(b.createdAt).getTime();
                          return dateSortOrder === "newest" ? dateB - dateA : dateA - dateB;
                        })
                      )}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
            </>
          )}
          </Tabs>
          <div className="flex flex-col items-center gap-2 mt-4">
            <p className="text-sm text-muted-foreground" aria-live="polite">
              Showing {workOrders.length} of {workOrdersTotalCount}
            </p>
            {!workOrdersLoading && hasNextPage && (
              <Button
                variant="outline"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                aria-label="Load more work orders"
                aria-busy={isFetchingNextPage}
              >
                {isFetchingNextPage ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Loading...
                  </>
                ) : (
                  "Load more"
                )}
              </Button>
            )}
          </div>
        </div>
      
      {showCreateModal && (
        <CreateWorkOrderModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateWorkOrder}
          isLoading={createWorkOrderMutation.isPending}
          users={users || []}
          currentUserId={user?.id}
        />
      )}
      
      {showEditModal && selectedWorkOrder && (
        <EditWorkOrderModal
          open={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedWorkOrder(null);
          }}
          onSubmit={handleUpdateWorkOrder}
          isLoading={updateWorkOrderMutation.isPending}
          workOrder={selectedWorkOrder}
          users={users || []}
        />
      )}
    </ResponsiveLayout>
  );
}
